import { Loader, Footer, CausesPortalLogo } from '@percent/cause-dashboard/common/components'
import styles from './AgentVerificationDocuments.module.scss'
import { AgentVerificationDocumentsStatus } from './agentVerificationDocumentsStatus/AgentVerificationDocumentsStatus'
import { useLocation } from 'react-router-dom'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { AgentVerificationDocuments } from './AgentVerificationDocuments'
import {
  AgentVerificationRejectionReasonBucket,
  AgentVerificationStatus
} from '@percent/cause-dashboard/services/agentVerification/AgentVerification.types'
import { AVDocumentsStatus } from './agentVerificationDocumentsStatus/AgentVerificationDocumentsStatus.types'
import { ReuploadView } from './agentVerificationDocumentReupload/ReuploadView'
import { useState } from 'react'
import { useQuery, useSitewideMessage } from '@percent/cause-dashboard/common/hooks'
import classNames from 'classnames/bind'

const REUPLOAD_REASON_BUCKETS: AgentVerificationRejectionReasonBucket[] = [
  AgentVerificationRejectionReasonBucket.Unclear,
  AgentVerificationRejectionReasonBucket.WrongDocument,
  AgentVerificationRejectionReasonBucket.InsufficientInformation
]

const mapDocumentUploadStatus = (
  status: AgentVerificationStatus | undefined,
  hasError: boolean,
  documentsSubmitted: boolean
): AVDocumentsStatus => {
  if (documentsSubmitted) return AVDocumentsStatus.SUBMITTED

  if (status === AgentVerificationStatus.PENDING_USER_VERIFICATION || hasError || !status)
    return AVDocumentsStatus.EXPIRED

  if ([AgentVerificationStatus.PENDING_REVIEW, AgentVerificationStatus.PENDING].includes(status))
    return AVDocumentsStatus.PENDING

  return AVDocumentsStatus.PROCESSED
}

export const AgentVerificationDocumentsContainer = () => {
  const { shouldShowSitewideMessage } = useSitewideMessage()
  const [documentsSubmitted, setDocumentsSubmitted] = useState(false)
  const { search } = useLocation()
  const { agentVerificationService } = useServices()

  const agentVerificationToken = new URLSearchParams(search).get('token') || ''

  const [{ data: agentVerificationDocumentStatus, isLoading, error }, { refresh }] = useQuery(
    agentVerificationService.getAgentVerificationDocumentStatus,
    agentVerificationToken
  )

  const [{ data: agentVerificationConfirmation, isLoading: isLoadingAgent }] = useQuery(
    agentVerificationService.getConfirmAgentVerificationRequest,
    agentVerificationToken
  )

  const onDocumentsSubmitted = () => {
    setDocumentsSubmitted(true)
    refresh()
  }

  // On re-upload success we fall through to the shared submitted status screen via the
  // `!documentsSubmitted` gate on the re-upload branch below (no refresh needed). We deliberately
  // skip refresh() so a re-fetch can't change rejectionReason and disturb the routing.
  const onReuploadSubmitted = () => setDocumentsSubmitted(true)

  const statusData = agentVerificationDocumentStatus?.data
  const status = mapDocumentUploadStatus(statusData?.status, !!error, documentsSubmitted)
  const shouldDisplayStatusScreen =
    statusData?.status &&
    [
      AgentVerificationStatus.APPROVED,
      AgentVerificationStatus.REJECTED,
      AgentVerificationStatus.PENDING_USER_VERIFICATION
    ].includes(statusData?.status)

  // Re-upload screens are only valid when the config offers re-upload; otherwise fall through to the shared status screen. (AV-2594)
  const isReuploadContext =
    !error &&
    !!statusData?.documentReuploadEnabled &&
    !!statusData?.rejectionReason &&
    REUPLOAD_REASON_BUCKETS.includes(statusData.rejectionReason)
  // Cap exhausted (no attempts left, or already rejected): show the shared status screen, not the form.
  const isReuploadCapExhausted =
    isReuploadContext &&
    !!statusData &&
    (statusData.attemptsRemaining <= 0 || statusData.status === AgentVerificationStatus.REJECTED)
  const isReuploadAwaitingUpload = isReuploadContext && statusData?.awaitingDocumentUpload !== false

  const agentData = agentVerificationConfirmation?.data

  if ((isLoadingAgent || isLoading) && !statusData) return <Loader />

  const cx = classNames.bind(styles)

  const renderContent = () => {
    if (isReuploadCapExhausted) {
      return (
        <AgentVerificationDocumentsStatus
          status={AVDocumentsStatus.CAP_EXHAUSTED}
          partnerName={statusData?.partnerName}
          organisationId={agentData?.data?.organisationId}
          partnerBanner={statusData?.partnerBanner}
        />
      )
    }

    if (isReuploadAwaitingUpload && statusData && !documentsSubmitted) {
      return (
        <ReuploadView
          agentVerificationToken={agentVerificationToken}
          statusData={statusData}
          onDocumentsSubmitted={onReuploadSubmitted}
        />
      )
    }

    if (
      statusData &&
      !shouldDisplayStatusScreen &&
      !statusData?.hasUploadedDocuments &&
      !error &&
      !documentsSubmitted
    ) {
      return (
        <AgentVerificationDocuments
          agentVerificationToken={agentVerificationToken}
          agentVerificationId={statusData?.id}
          onDocumentsSubmitted={onDocumentsSubmitted}
          organisationName={statusData?.organisationName}
          useDropzone
          partnerBanner={statusData?.partnerBanner}
          programType={statusData?.programType}
        />
      )
    }

    return (
      <AgentVerificationDocumentsStatus
        status={status}
        agentVerificationStatus={statusData?.status}
        partnerName={statusData?.partnerName}
        organisationId={agentData?.data?.organisationId}
        partnerBanner={statusData?.partnerBanner}
      />
    )
  }

  return (
    <div
      className={cx({
        agentVerificationDocumentsContainerDefault: !shouldShowSitewideMessage,
        agentVerificationDocumentsContainerWithBanner: shouldShowSitewideMessage
      })}
    >
      <CausesPortalLogo />
      {renderContent()}

      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  )
}
